<!-- Console 0.1.151; 2026-10-22 15:25 CST; parent e6b836238891324fdbf0b39406d3cc0a4b5f835f. Persistent IP editor feedback follows saved-version receipts and preserves in-flight edits. -->
<!-- Console 0.1.150; 2026-10-22 14:52 CST; parent ee28d82c0f6d228650bdffd99d530f5227d8e70c. Read-only receipt refresh; pending is not failure; preserve drafts. -->
<!-- Console 0.1.148; 2026-10-14 13:35 CST; parent d2af4951d0ef07f464eb115860b6cfeefc0d6d54. Republish the latest saved policy unchanged through the normal versioned flow. -->
<!-- 当前运营控制台策略工作区，版本 0.1.78，更新时间：2026-09-05 22:43 CST。IP 编辑区取消包裹交互控件的 label，彻底阻止点击底部空白时浏览器自动激活首个删除按钮。 -->
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { ElMessageBox } from 'element-plus';
import { consoleApi } from '../api/client';
import { publishCurrentDomainConfig } from '../utils/autoPublish';
import { createOperationTiming } from '../utils/operationTiming';
import type {
  Domain,
  IpCollection,
  SecurityStrategy,
  SecurityStrategyConfiguration,
  SecurityStrategyDomainResult,
  SecurityStrategySimulationResponse,
  SecurityWhitelistMode
} from '../api/types';

const DIRECT_IP_PRIORITY = 1000;
const IP_COLLECTION_PRIORITY = 900;
const COUNTRY_PRIORITY = 800;
const UNKNOWN_REGION_PRIORITY = 700;
const DEFAULT_PRIORITY = 100;
const AUTO_PUBLISH_DELAY = 1000;
// 首次采用 2026-09-05 线上实测约 20 秒；以后按当前浏览器最近 30 次成功 ACK 自动校准。
const policyPublishTiming = createOperationTiming('hotx:strategy-publish-timing:v1', 20_000);
const publishTimingHint = policyPublishTiming.hint;

type IpRuleList = 'whitelistCidrs' | 'blacklistCidrs';
type IpRuleChanges = Record<IpRuleList, { added: Set<string>; removed: Set<string> }>;
const pendingIpChanges = reactive<IpRuleChanges>({
  whitelistCidrs: { added: new Set(), removed: new Set() },
  blacklistCidrs: { added: new Set(), removed: new Set() }
});
const protectedRemovalUntil: Record<IpRuleList, number> = {
  whitelistCidrs: 0,
  blacklistCidrs: 0
};
interface RemovedRuleDraft { cidr: string; wasAdded: boolean }
const removalHistory = reactive<Record<IpRuleList, RemovedRuleDraft[]>>({
  whitelistCidrs: [],
  blacklistCidrs: []
});

const domains = ref<Domain[]>([]);
const strategies = ref<SecurityStrategy[]>([]);
const selectedStrategyId = ref<number>();
const strategySelectValue = ref<number>();
const ipCollections = ref<IpCollection[]>([]);
const configuration = ref<SecurityStrategyConfiguration>(emptyConfiguration());
const lastGoodConfiguration = ref<SecurityStrategyConfiguration>(emptyConfiguration());
const directAllowInput = ref('');
const directBlockInput = ref('');
const whitelistInputElement = ref<HTMLTextAreaElement | null>(null);
const blacklistInputElement = ref<HTMLTextAreaElement | null>(null);
const simulateIp = ref('');
const simulation = ref<SecurityStrategySimulationResponse | null>(null);
const loading = ref(false);
const publishing = ref(false);
const suppressChanges = ref(false);
const publishState = ref('');
const timingSection = ref('');
const error = ref('');
const success = ref('');
type PublicationPhase = 'idle' | 'queued' | 'saving' | 'publishing' | 'pending' | 'active' | 'failed' | 'unknown' | 'changed';
const publicationPhase = ref<PublicationPhase>('idle');
const publicationError = ref('');
const confirmedAt = ref('');
const ipOperationNotes = reactive<Record<IpRuleList, string>>({ whitelistCidrs: '', blacklistCidrs: '' });
const ipInputErrors = reactive<Record<IpRuleList, string>>({ whitelistCidrs: '', blacklistCidrs: '' });

function applyReceiptPhase(status: string) {
  publicationPhase.value = status === 'ACTIVE' ? 'active'
    : ['FAILED', 'PARTIAL_FAILED'].includes(status) ? 'failed'
    : ['PREPARED', 'PUBLISHING'].includes(status) ? 'pending' : 'idle';
  if (status === 'ACTIVE') confirmedAt.value = new Date().toLocaleTimeString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false });
}

function feedbackFor(type: IpRuleList) {
  const label = type === 'whitelistCidrs' ? '白名单' : '黑名单';
  const input = type === 'whitelistCidrs' ? directAllowInput.value : directBlockInput.value;
  if (ipInputErrors[type]) return { phase: 'failed', title: `${label}输入未添加`, detail: ipInputErrors[type] };
  if (input.trim()) return { phase: 'queued', title: `${label}尚未添加`, detail: '按回车或点击“添加 IP”后才会保存。' };
  const labels: Record<PublicationPhase, string> = {
    idle: '尚未发布', queued: '待保存', saving: '正在保存', publishing: '正在下发', pending: '等待节点确认',
    active: '已生效', failed: '修改未确认生效', unknown: '正在确认，状态暂未刷新', changed: '服务端版本已更新'
  };
  const details: Record<PublicationPhase, string> = {
    idle: '当前策略还没有发布记录。',
    queued: publishing.value ? '新修改已排队，当前发布完成后自动保存。' : '静置 1 秒后自动保存。',
    saving: '正在保存配置，尚未确认节点生效。',
    publishing: '配置已保存，正在下发；收到节点确认后才算生效。',
    pending: selectedStrategy.value?.publishMessage || '配置已保存，等待节点确认；结果会自动更新。',
    active: `节点已确认当前已保存配置；最近确认 ${confirmedAt.value}。`,
    failed: publicationError.value || selectedStrategy.value?.publishMessage || '请核对错误原因后重新操作。',
    unknown: '正在自动重试查询，暂不能确认是否生效；不会重复提交。',
    changed: '另一页面已保存新版本；请保留当前编辑并重新载入核对。'
  };
  return { phase: publicationPhase.value, title: `${label}${labels[publicationPhase.value]}`,
    detail: [ipOperationNotes[type], details[publicationPhase.value]].filter(Boolean).join('；') };
}

const createVisible = ref(false);
const manageVisible = ref(false);
const createName = ref('');
const createDomainIds = ref<number[]>([]);
const manageName = ref('');
const manageDomainIds = ref<number[]>([]);
let publishTimer: ReturnType<typeof setTimeout> | undefined;
let pendingSection = '';
let configurationRevision = 0;
let publishQueued = false;
let receiptTimer: ReturnType<typeof setTimeout> | undefined;
let disposed = false;
let awaitingReceipt: { id: number; version: number; revision: number } | undefined;

// Only receipt metadata changes here: never replace the editable configuration.
async function refreshReceipt() {
  const strategy = selectedStrategy.value;
  const revision = configurationRevision;
  try {
    if (!strategy || publishing.value || publishTimer || publishQueued
        || ['failed', 'changed'].includes(publicationPhase.value)
        || !['PREPARED', 'PUBLISHING'].includes(strategy.publishStatus)) return;
    const rows = await consoleApi.security.strategies();
    if (disposed || publishing.value || publishTimer || publishQueued
        || selectedStrategy.value !== strategy || selectedStrategyId.value !== strategy.id || configurationRevision !== revision) return;
    const latest = rows.find(row => row.id === strategy.id);
    if (!latest || latest.versionNo < strategy.versionNo) return;
    if (latest.versionNo > strategy.versionNo) {
      publicationPhase.value = 'changed';
      publishState.value = '服务端版本已更新，请核对策略';
      return;
    }
    applyReceiptPhase(latest.publishStatus);
    strategy.publishStatus = latest.publishStatus;
    strategy.publishMessage = latest.publishMessage;
    publishState.value = statusLabel(latest.publishStatus);
    if (['FAILED', 'PARTIAL_FAILED'].includes(latest.publishStatus)) {
      error.value = latest.publishMessage || '节点发布失败，请查看节点回执';
      publicationError.value = error.value;
    }
    if (awaitingReceipt?.id === latest.id && awaitingReceipt.version === latest.versionNo) {
      if (latest.publishStatus === 'ACTIVE') {
        if (configurationRevision === awaitingReceipt.revision) policyPublishTiming.succeed();
        if (success.value === '配置已保存，正在等待节点确认；页面会自动刷新结果。') success.value = '节点已确认，策略已生效。';
        awaitingReceipt = undefined;
      } else if (['FAILED', 'PARTIAL_FAILED'].includes(latest.publishStatus)) {
        if (configurationRevision === awaitingReceipt.revision) policyPublishTiming.fail();
        awaitingReceipt = undefined;
      }
    }
  } catch {
    if (!disposed && selectedStrategyId.value === strategy?.id && configurationRevision === revision
        && !publishing.value && !publishTimer) {
      publicationPhase.value = 'unknown';
      publishState.value = '正在确认，状态暂未刷新，将自动重试';
    }
  } finally {
    if (!disposed) receiptTimer = setTimeout(() => { void refreshReceipt(); }, 3000);
  }
}

const selectedStrategy = computed(() => strategies.value.find(item => item.id === selectedStrategyId.value));
const boundDomainNames = computed(() => selectedStrategy.value?.domains.map(item => item.domain).join('、') || '未绑定域名');
const availableDomains = computed(() => domains.value.map(domain => ({
  ...domain,
  strategyName: strategies.value.find(strategy => strategy.domainIds.includes(domain.id))?.name
})));

const countryOptions = [
  { code: 'CN', name: '中国大陆' }, { code: 'HK', name: '中国香港' },
  { code: 'MO', name: '中国澳门' }, { code: 'TW', name: '中国台湾' },
  { code: 'US', name: '美国' }, { code: 'SG', name: '新加坡' },
  { code: 'MY', name: '马来西亚' }, { code: 'VN', name: '越南' },
  { code: 'TH', name: '泰国' }, { code: 'JP', name: '日本' },
  { code: 'KR', name: '韩国' }, { code: 'ID', name: '印度尼西亚' },
  { code: 'PH', name: '菲律宾' }, { code: 'IN', name: '印度' },
  { code: 'GB', name: '英国' }, { code: 'DE', name: '德国' },
  { code: 'FR', name: '法国' }, { code: 'BR', name: '巴西' },
  { code: 'RU', name: '俄罗斯' }, { code: 'IR', name: '伊朗' },
  { code: 'KP', name: '朝鲜' }
];

const priorityLayers = [
  { weight: DIRECT_IP_PRIORITY, title: '个别 IP / 网段', subtitle: '黑名单最高优先，白名单按所选模式放行', tone: 'danger' },
  { weight: IP_COLLECTION_PRIORITY, title: 'IP 集合', subtitle: '公共集合可以被多个策略引用', tone: 'warning' },
  { weight: COUNTRY_PRIORITY, title: '国家/地区', subtitle: '按中文国家选择，系统保存 ISO 两位代码', tone: 'success' },
  { weight: UNKNOWN_REGION_PRIORITY, title: '未知地区', subtitle: 'GeoIP 无法识别时执行', tone: 'info' },
  { weight: DEFAULT_PRIORITY, title: '默认放行', subtitle: '未命中限制时允许访问', tone: 'muted' }
];

onMounted(() => {
  // 捕获页面内的外部点击，避免普通文字或空白区域不触发 textarea blur 的浏览器差异。
  document.addEventListener('pointerdown', handleIpEditorOutsidePointerDown, true);
  void loadWorkspace();
  receiptTimer = setTimeout(() => { void refreshReceipt(); }, 3000);
});
onBeforeUnmount(() => {
  disposed = true;
  if (receiptTimer) clearTimeout(receiptTimer);
  if (publishTimer) clearTimeout(publishTimer);
  document.removeEventListener('pointerdown', handleIpEditorOutsidePointerDown, true);
  policyPublishTiming.dispose();
});
onBeforeRouteLeave(async () => {
  await flushPendingChanges();
  return true;
});

/**
 * 切换策略前先发布旧策略的待保存内容，避免异步期间把旧配置写进新策略。
 */
async function changeSelectedStrategy(next: number) {
  strategySelectValue.value = selectedStrategyId.value;
  await flushPendingChanges();
  selectedStrategyId.value = next;
  strategySelectValue.value = next;
  loadSelectedStrategy();
}

async function loadWorkspace() {
  loading.value = true;
  try {
    const [domainPage, strategyRows, collections] = await Promise.all([
      consoleApi.portal.domains(),
      consoleApi.security.strategies(),
      consoleApi.security.ipCollections().catch(() => [] as IpCollection[])
    ]);
    const deliverable = await Promise.all((domainPage.domains || []).map(async domain => {
      const origins = await consoleApi.origins.list(domain.id).catch(() => []);
      return { ...domain, originCount: origins.length };
    }));
    domains.value = deliverable.filter(domain =>
      domain.accessMode === 'REDIRECT_301' || Number(domain.originCount || 0) > 0
    ) as Domain[];
    strategies.value = strategyRows;
    ipCollections.value = collections;
    selectedStrategyId.value = strategyRows[0]?.id;
    strategySelectValue.value = selectedStrategyId.value;
    loadSelectedStrategy();
  } catch (reason) {
    error.value = messageOf(reason, '策略工作区加载失败');
  } finally {
    loading.value = false;
  }
}

function loadSelectedStrategy() {
  suppressChanges.value = true;
  clearPendingIpChanges();
  const strategy = selectedStrategy.value;
  configuration.value = strategy ? cloneConfiguration(strategy.configuration) : emptyConfiguration();
  lastGoodConfiguration.value = cloneConfiguration(configuration.value);
  simulation.value = null;
  publishState.value = strategy ? statusLabel(strategy.publishStatus) : '';
  applyReceiptPhase(strategy?.publishStatus || 'IDLE');
  publicationError.value = '';
  (['whitelistCidrs', 'blacklistCidrs'] as IpRuleList[]).forEach(type => {
    ipOperationNotes[type] = '';
    ipInputErrors[type] = '';
  });
  queueMicrotask(() => { suppressChanges.value = false; });
}

function openCreate() {
  createName.value = '';
  createDomainIds.value = [];
  createVisible.value = true;
}

async function createStrategy() {
  if (!validName(createName.value)) return;
  loading.value = true;
  try {
    const result = await consoleApi.security.createStrategy({
      name: createName.value.trim(), domainIds: createDomainIds.value
    });
    createVisible.value = false;
    await publishPrepared(result.strategy.id, result.domainResults, `创建策略 ${result.strategy.name}`);
    strategies.value = await consoleApi.security.strategies();
    selectedStrategyId.value = result.strategy.id;
    strategySelectValue.value = selectedStrategyId.value;
    loadSelectedStrategy();
    success.value = `策略“${result.strategy.name}”已创建并切换。`;
  } catch (reason) {
    error.value = messageOf(reason, '策略创建失败');
  } finally {
    loading.value = false;
  }
}

function openManage() {
  if (!selectedStrategy.value) return;
  manageName.value = selectedStrategy.value.name;
  manageDomainIds.value = [...selectedStrategy.value.domainIds];
  manageVisible.value = true;
}

async function updateStrategy() {
  if (!selectedStrategy.value || !validName(manageName.value)) return;
  loading.value = true;
  try {
    await flushPendingChanges();
    const result = await consoleApi.security.updateStrategy(selectedStrategy.value.id, {
      name: manageName.value.trim(), domainIds: manageDomainIds.value
    });
    await publishPrepared(result.strategy.id, result.domainResults, `调整策略 ${result.strategy.name} 的域名绑定`);
    manageVisible.value = false;
    strategies.value = await consoleApi.security.strategies();
    selectedStrategyId.value = result.strategy.id;
    strategySelectValue.value = selectedStrategyId.value;
    loadSelectedStrategy();
    success.value = '策略名称和域名绑定已更新。';
  } catch (reason) {
    error.value = messageOf(reason, '策略管理保存失败');
  } finally {
    loading.value = false;
  }
}

async function deleteStrategy() {
  const strategy = selectedStrategy.value;
  if (!strategy) return;
  try {
    const prompt = await ElMessageBox.prompt(
      `删除后，${strategy.domainIds.length} 个绑定域名将立即恢复默认放行。请输入策略名称确认：${strategy.name}`,
      '复制策略名称确认删除',
      { confirmButtonText: '确认删除', cancelButtonText: '取消', inputPlaceholder: strategy.name }
    );
    if (prompt.value !== strategy.name) throw new Error('输入的策略名称不一致，未执行删除');
    loading.value = true;
    const result = await consoleApi.security.deleteStrategy(strategy.id, prompt.value);
    const receipt = await publishPrepared(strategy.id, result.domainResults, `删除策略 ${strategy.name} 并恢复默认放行`, false);
    manageVisible.value = false;
    strategies.value = await consoleApi.security.strategies();
    selectedStrategyId.value = strategies.value[0]?.id;
    strategySelectValue.value = selectedStrategyId.value;
    loadSelectedStrategy();
    success.value = receipt?.publishStatus === 'PUBLISHING' ? '策略已删除，域名恢复配置已提交，等待节点确认。' : '策略已删除，相关域名已解除策略限制。';
  } catch (reason) {
    if (String(reason).includes('cancel')) return;
    error.value = messageOf(reason, '策略删除失败');
  } finally {
    loading.value = false;
  }
}

function addRules(type: IpRuleList) {
  const input = type === 'whitelistCidrs' ? directAllowInput : directBlockInput;
  const values = parseIpInput(input.value);
  if (!values.length) return;
  ipInputErrors[type] = '';
  const opposite = type === 'whitelistCidrs' ? configuration.value.blacklistCidrs : configuration.value.whitelistCidrs;
  if (values.some(value => opposite.includes(value))) {
    error.value = '同一个 IP / 网段不能同时加入白名单和黑名单';
    ipInputErrors[type] = error.value;
    return;
  }
  const additions = values.filter(value => !configuration.value[type].includes(value));
  configuration.value[type] = [...new Set([...configuration.value[type], ...values])];
  additions.forEach(value => {
    pendingIpChanges[type].removed.delete(value);
    pendingIpChanges[type].added.add(value);
    const removalIndex = removalHistory[type].findIndex(item => item.cidr === value);
    if (removalIndex >= 0) removalHistory[type].splice(removalIndex, 1);
  });
  input.value = '';
  ipOperationNotes[type] = additions.length ? `本次新增 ${additions.length} 条` : '输入项已在列表中，未重复添加';
  if (additions.length) schedulePublish(type === 'whitelistCidrs' ? '白名单' : '黑名单');
}

async function removeRule(type: IpRuleList, cidr: string) {
  // 外部点击刚用于保存待输入 IP 时，组件随后补发的 close 事件必须被拒绝，避免一次点击又保存又误删。
  if (Date.now() <= protectedRemovalUntil[type]) {
    protectedRemovalUntil[type] = 0;
    return;
  }
  const wasAdded = pendingIpChanges[type].added.has(cidr);
  if (wasAdded) {
    pendingIpChanges[type].added.delete(cidr);
  }
  const remaining = configuration.value[type].filter(item => item !== cidr);
  if (type === 'whitelistCidrs'
      && configuration.value.ipWhitelistMode === 'ONLY_WHITELIST'
      && remaining.length === 0) {
    try {
      await ElMessageBox.confirm(
        '这是当前策略最后一条白名单。删除并发布后，未配置白名单时将恢复默认放行。确认继续删除吗？',
        '确认删除最后一条白名单',
        { confirmButtonText: '确认删除', cancelButtonText: '保留白名单', type: 'warning' }
      );
    } catch {
      return;
    }
  }
  if (!wasAdded) pendingIpChanges[type].removed.add(cidr);
  // 删除后标签立即消失；真正发布前保留本版块撤销记录，兼顾即时反馈与误删恢复。
  removalHistory[type].push({ cidr, wasAdded });
  configuration.value[type] = remaining;
  ipInputErrors[type] = '';
  ipOperationNotes[type] = `本次删除 ${removalHistory[type].length} 条`;
  schedulePublish(type === 'whitelistCidrs' ? '白名单' : '黑名单');
}

function undoRuleRemovals(type: IpRuleList) {
  const removed = [...removalHistory[type]];
  if (!removed.length) return;
  const restored = [...configuration.value[type]];
  removed.forEach(item => {
    if (!restored.includes(item.cidr)) restored.push(item.cidr);
    if (item.wasAdded) pendingIpChanges[type].added.add(item.cidr);
    else pendingIpChanges[type].removed.delete(item.cidr);
  });
  configuration.value[type] = restored;
  removalHistory[type].splice(0);
  ipOperationNotes[type] = `本次撤销删除 ${removed.length} 条`;
  schedulePublish(type === 'whitelistCidrs' ? '白名单' : '黑名单');
}

function activeRuleCount(type: IpRuleList) {
  return configuration.value[type].length;
}

/** Google Cloud 风格：粘贴后保留可编辑文本，仅在用户按回车时提交。 */
function handleIpEditorKeydown(type: IpRuleList, event: KeyboardEvent) {
  // 仅回车确认，避免用户输入空格或逗号修改粘贴内容时被意外提交。
  if (event.key !== 'Enter') return;
  event.preventDefault();
  addRules(type);
}

/**
 * 点击真实文本输入区外部时保存待输入 IP。
 * 若这次点击刚好落在旧标签的删除叉号上，捕获阶段会拦截删除，确保一次点击只执行保存。
 */
function handleIpEditorOutsidePointerDown(event: PointerEvent) {
  const target = event.target as Node | null;
  if (!target) return;
  const targetElement = target instanceof Element ? target : target.parentElement;
  const pendingWhitelist = Boolean(directAllowInput.value.trim());
  const pendingBlacklist = Boolean(directBlockInput.value.trim());
  if (pendingWhitelist && !whitelistInputElement.value?.contains(target)) {
    const protectedClose = targetElement?.closest('[data-test="whitelist-ip-editor"] .el-tag__close');
    if (protectedClose) protectedRemovalUntil.whitelistCidrs = Date.now() + 1000;
    addRules('whitelistCidrs');
    if (protectedClose) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
  if (pendingBlacklist && !blacklistInputElement.value?.contains(target)) {
    const protectedClose = targetElement?.closest('[data-test="blacklist-ip-editor"] .el-tag__close');
    if (protectedClose) protectedRemovalUntil.blacklistCidrs = Date.now() + 1000;
    addRules('blacklistCidrs');
    if (protectedClose) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
}

function clearPendingIpChanges() {
  (['whitelistCidrs', 'blacklistCidrs'] as IpRuleList[]).forEach(type => {
    pendingIpChanges[type].added.clear();
    pendingIpChanges[type].removed.clear();
    removalHistory[type].splice(0);
  });
}

/** 为当前发布批次复制增删记录，避免发布期间的新操作混入旧请求。 */
function snapshotIpChanges(): IpRuleChanges {
  return {
    whitelistCidrs: {
      added: new Set(pendingIpChanges.whitelistCidrs.added),
      removed: new Set(pendingIpChanges.whitelistCidrs.removed)
    },
    blacklistCidrs: {
      added: new Set(pendingIpChanges.blacklistCidrs.added),
      removed: new Set(pendingIpChanges.blacklistCidrs.removed)
    }
  };
}

/**
 * 一批配置落库后，以该批结果为新基线重新计算尚未发布的 IP 差异。
 * 这保证“新增后立即删除”或“删除后立即撤销”在前一批仍发布时也不会被旧响应复活或抹掉。
 */
function syncPendingIpChanges(local: SecurityStrategyConfiguration, base: SecurityStrategyConfiguration) {
  (['whitelistCidrs', 'blacklistCidrs'] as IpRuleList[]).forEach(type => {
    const localValues = new Set(local[type]);
    const baseValues = new Set(base[type]);
    pendingIpChanges[type].added.clear();
    pendingIpChanges[type].removed.clear();
    localValues.forEach(value => { if (!baseValues.has(value)) pendingIpChanges[type].added.add(value); });
    baseValues.forEach(value => { if (!localValues.has(value)) pendingIpChanges[type].removed.add(value); });
  });
}

/** 仅把当前发布批次明确的 IP 增删叠加到服务端最新版，避免旧页面整份覆盖。 */
function rebaseIpChanges(local: SecurityStrategyConfiguration, latest: SecurityStrategyConfiguration,
                         changes: IpRuleChanges) {
  const rebased = cloneConfiguration(local);
  (['whitelistCidrs', 'blacklistCidrs'] as IpRuleList[]).forEach(type => {
    const values = new Set(latest[type]);
    changes[type].removed.forEach(value => values.delete(value));
    changes[type].added.forEach(value => values.add(value));
    rebased[type] = [...values];
  });
  return rebased;
}

function schedulePublish(section: string) {
  if (!selectedStrategy.value || suppressChanges.value) return;
  if (publishTimer) clearTimeout(publishTimer);
  configurationRevision += 1;
  publicationPhase.value = 'queued';
  publicationError.value = '';
  policyPublishTiming.start();
  pendingSection = section;
  timingSection.value = section;
  publishState.value = `${section}等待发布，1 秒`;
  publishTimer = setTimeout(() => {
    publishTimer = undefined;
    void saveAndPublish();
  }, AUTO_PUBLISH_DELAY);
}

function showTimingFor(section: string) {
  return timingSection.value === section && Boolean(publishTimingHint.value);
}

async function flushPendingChanges() {
  if (!publishTimer) return;
  clearTimeout(publishTimer);
  publishTimer = undefined;
  await saveAndPublish();
}

async function republishCurrent() {
  if (publishing.value || loading.value || !selectedStrategy.value) return;
  if (publishTimer || directAllowInput.value.trim() || directBlockInput.value.trim()
      || JSON.stringify(configuration.value) !== JSON.stringify(lastGoodConfiguration.value)) {
    error.value = '请先等待当前编辑发布完成，再重新发布已保存策略';
    return;
  }
  await saveAndPublish(true);
}

async function saveAndPublish(useSavedConfiguration = false) {
  const strategy = selectedStrategy.value;
  if (!strategy) return;
  // 节点发布只能串行执行；发布期间产生的新修改进入下一批，不锁界面也不丢操作。
  if (publishing.value) {
    publishQueued = true;
    return;
  }
  publishQueued = false;
  const publishingRevision = configurationRevision;
  const publishingSection = pendingSection || '策略';
  const localSnapshot = cloneConfiguration(configuration.value);
  const ipChangesSnapshot = snapshotIpChanges();
  publishing.value = true;
  publicationPhase.value = 'saving';
  publicationError.value = '';
  error.value = '';
  publishState.value = `${publishingSection}正在后台生效`;
  let storedConfiguration: SecurityStrategyConfiguration | undefined;
  try {
    // 发布前读取最新版并携带版本号；旧标签页只会合并自己的增删，不再整份覆盖新记录。
    const latestRows = await consoleApi.security.strategies();
    const latest = latestRows.find(item => item.id === strategy.id);
    if (!latest) throw new Error('当前策略已不存在，请刷新后重试');
    const publishConfiguration = useSavedConfiguration
      ? cloneConfiguration(latest.configuration)
      : rebaseIpChanges(localSnapshot, latest.configuration, ipChangesSnapshot);
    publishConfiguration.expectedVersionNo = latest.versionNo;
    assertNoConflict(publishConfiguration);
    const result = await consoleApi.security.updateStrategyConfiguration(strategy.id, publishConfiguration);
    storedConfiguration = cloneConfiguration(publishConfiguration);
    delete storedConfiguration.expectedVersionNo;
    if (selectedStrategyId.value === strategy.id && configurationRevision === publishingRevision) publicationPhase.value = 'publishing';
    const receipt = await publishPrepared(strategy.id, result.domainResults, `策略 ${strategy.name} ${useSavedConfiguration ? "重新发布" : "自动发布"}`);
    strategies.value = await consoleApi.security.strategies();
    const finalReceipt = strategies.value.find(item => item.id === strategy.id);
    // A later tab may have saved again while this request was awaiting node receipts.
    // Its ACTIVE is not confirmation of this operation; keep the user's editor intact.
    if (finalReceipt && finalReceipt.versionNo > result.strategy.versionNo) {
      if (selectedStrategyId.value === strategy.id && configurationRevision === publishingRevision) {
        publicationPhase.value = 'changed';
        publishState.value = '服务端版本已更新，请核对策略';
      }
      awaitingReceipt = undefined;
      return;
    }
    if (finalReceipt && ['FAILED', 'PARTIAL_FAILED'].includes(finalReceipt.publishStatus)) {
      throw new Error(finalReceipt.publishMessage || '节点发布失败');
    }
    // 只有本批发布期间没有新修改时才用服务端结果更新当前版块；否则保留用户正在编辑的新状态。
    if (selectedStrategyId.value === strategy.id && configurationRevision === publishingRevision) {
      suppressChanges.value = true;
      configuration.value = cloneConfiguration(selectedStrategy.value?.configuration || storedConfiguration);
      lastGoodConfiguration.value = cloneConfiguration(configuration.value);
      clearPendingIpChanges();
      queueMicrotask(() => { suppressChanges.value = false; });
      const currentStatus = selectedStrategy.value?.publishStatus || receipt?.publishStatus || 'PUBLISHING';
      publishState.value = statusLabel(currentStatus);
      applyReceiptPhase(currentStatus);
    } else {
      lastGoodConfiguration.value = cloneConfiguration(storedConfiguration);
      syncPendingIpChanges(configuration.value, storedConfiguration);
      publishState.value = `${pendingSection || '策略'}等待继续发布`;
      if (selectedStrategyId.value === strategy.id) publicationPhase.value = 'queued';
    }
    const current = strategies.value.find(item => item.id === strategy.id);
    if (current?.publishStatus === 'ACTIVE') {
      if (configurationRevision === publishingRevision) policyPublishTiming.succeed();
      awaitingReceipt = undefined;
    } else {
      awaitingReceipt = { id: strategy.id, version: current?.versionNo || result.strategy.versionNo, revision: publishingRevision };
    }
    success.value = current?.publishStatus === 'ACTIVE' ? `策略“${strategy.name}”已${useSavedConfiguration ? "重新" : "自动"}发布。` : '配置已保存，正在等待节点确认；页面会自动刷新结果。';
  } catch (reason) {
    awaitingReceipt = undefined;
    policyPublishTiming.fail();
    // 新修改存在时禁止旧请求失败回滚整个页面，否则会再次造成用户刚输入的内容消失。
    if (selectedStrategyId.value === strategy.id && configurationRevision === publishingRevision) {
      suppressChanges.value = true;
      configuration.value = cloneConfiguration(storedConfiguration || lastGoodConfiguration.value);
      if (storedConfiguration) lastGoodConfiguration.value = cloneConfiguration(storedConfiguration);
      clearPendingIpChanges();
      queueMicrotask(() => { suppressChanges.value = false; });
    } else if (storedConfiguration) {
      lastGoodConfiguration.value = cloneConfiguration(storedConfiguration);
      syncPendingIpChanges(configuration.value, storedConfiguration);
    }
    publishState.value = storedConfiguration ? '配置已保存，节点发布尚未全部确认' : '保存失败，编辑状态已恢复';
    error.value = messageOf(reason, '策略发布失败');
    if (selectedStrategyId.value === strategy.id && configurationRevision === publishingRevision) {
      publicationPhase.value = 'failed';
      publicationError.value = error.value;
    }
  } finally {
    publishing.value = false;
    // 若 1 秒计时器已在上一轮发布期间触发，立即重新排队；已有计时器则由它自然接管。
    if (publishQueued && !publishTimer) {
      publishQueued = false;
      publishState.value = `${pendingSection || '策略'}等待发布，1 秒`;
      publishTimer = setTimeout(() => {
        publishTimer = undefined;
        void saveAndPublish();
      }, AUTO_PUBLISH_DELAY);
    } else if (!publishTimer && configurationRevision === publishingRevision) {
      publishQueued = false;
      pendingSection = '';
    }
  }
}

async function publishPrepared(strategyId: number, results: SecurityStrategyDomainResult[], reason: string,
                               reportStatus = true) {
  const preparationFailures = results.filter(item => !item.prepared);
  const publishFailures: string[] = [];
  let pendingRelease = false;
  for (const item of results.filter(row => row.prepared)) {
    try {
      const outcome = await publishCurrentDomainConfig(item.domainId, reason, true);
      if (outcome?.pending) pendingRelease = true;
      if (!outcome?.ok && !outcome?.pending) {
        throw new Error(outcome?.message || '节点发布失败');
      }
    } catch (failure) {
      publishFailures.push(`${domainName(item.domainId)}：${messageOf(failure, '发布失败')}`);
    }
  }
  const failures = [
    ...preparationFailures.map(item => `${domainName(item.domainId)}：${item.message}`),
    ...publishFailures
  ];
  if (reportStatus) {
    // Security publication V1; 2026-10-14 13:04:22 CST; parent 0864a5de06d1e5e998bf205f23af0317f67d9476. Server receipts decide success.
    const actual = await consoleApi.security.updateStrategyPublishStatus(strategyId, {
      status: failures.length ? 'PARTIAL_FAILED' : 'ACTIVE',
      message: failures.join('；')
    });
    if (failures.length || ['FAILED', 'PARTIAL_FAILED'].includes(actual.publishStatus)) {
      throw new Error(actual.publishMessage || failures.join('；') || '节点发布失败');
    }
    return actual;
  }
  if (failures.length) throw new Error(failures.join('；'));
  return { publishStatus: pendingRelease ? 'PUBLISHING' : 'ACTIVE' };
}

async function runSimulation() {
  if (!selectedStrategy.value || !simulateIp.value.trim()) {
    error.value = selectedStrategy.value ? '请输入访问者 IP 后再模拟' : '请先创建并选择策略';
    return;
  }
  try {
    simulation.value = await consoleApi.security.simulateStrategy(selectedStrategy.value.id, {
      clientIp: simulateIp.value.trim()
    });
  } catch (reason) {
    error.value = messageOf(reason, '策略模拟暂时不可用');
  }
}

function assertNoConflict(value = configuration.value) {
  const countries = value.allowedCountries.filter(item => value.deniedCountries.includes(item));
  if (countries.length) throw new Error(`同一个国家不能同时允许和禁止：${countries.join('、')}`);
  const collections = value.whitelistCollectionIds.filter(item => value.blacklistCollectionIds.includes(item));
  if (collections.length) throw new Error('同一个 IP 集合不能同时允许和拦截');
}

function validName(value: string) {
  const name = value.trim();
  if (name.length < 2 || name.length > 30 || !/^[\p{L}\p{N} _-]+$/u.test(name)) {
    error.value = '策略名称需为 2 至 30 个中文、英文、数字、空格、短横线或下划线';
    return false;
  }
  return true;
}

function emptyConfiguration(): SecurityStrategyConfiguration {
  return {
    whitelistMode: 'PRIORITY_ALLOW', ipWhitelistMode: 'PRIORITY_ALLOW',
    collectionWhitelistMode: 'PRIORITY_ALLOW', countryWhitelistMode: 'PRIORITY_ALLOW',
    whitelistCidrs: [], blacklistCidrs: [],
    allowedCountries: [], deniedCountries: [], unknownCountryAction: 'OBSERVE',
    whitelistCollectionIds: [], blacklistCollectionIds: []
  };
}

function cloneConfiguration(value: SecurityStrategyConfiguration): SecurityStrategyConfiguration {
  return JSON.parse(JSON.stringify(value)) as SecurityStrategyConfiguration;
}

function parseIpInput(value: string) {
  return value.split(/[\n,，\s]+/).map(item => item.trim()).filter(Boolean);
}

function domainName(id: number) {
  return domains.value.find(domain => domain.id === id)?.domain || `域名 ${id}`;
}

function statusLabel(status: string) {
  if (status === 'ACTIVE') return '已生效';
  if (status === 'PARTIAL_FAILED' || status === 'FAILED') return '部分域名发布失败';
  if (status === 'PREPARED' || status === 'PUBLISHING') return '正在生效';
  return '尚未发布';
}

function messageOf(reason: unknown, fallback: string) {
  return reason instanceof Error && reason.message ? reason.message : fallback;
}

function modeLabel(mode: SecurityWhitelistMode) {
  return mode === 'ONLY_WHITELIST' ? '仅白名单可访问' : '白名单优先放行';
}
</script>

<template>
  <section class="page strategy-page">
    <div class="page-title page-title-row">
      <div>
        <h1>访问控制策略</h1>
        <p>创建可复用策略，一项策略可以绑定多个域名；各版块修改后静置 1 秒自动发布。</p>
      </div>
      <el-button type="primary" @click="openCreate">创建策略</el-button>
    </div>

    <el-alert v-if="error" :title="error" type="error" show-icon closable @close="error = ''" />
    <el-alert v-if="success" :title="success" type="success" show-icon closable @close="success = ''" />

    <section class="strategy-toolbar">
      <div class="strategy-picker">
        <span>当前策略</span>
        <el-select
          v-model="strategySelectValue"
          placeholder="请选择或创建策略"
          :loading="loading"
          @change="changeSelectedStrategy"
        >
          <el-option v-for="item in strategies" :key="item.id" :label="item.name" :value="item.id">
            <span>{{ item.name }}</span>
            <small>{{ item.domainIds.length }} 个域名</small>
          </el-option>
        </el-select>
      </div>
      <el-button :disabled="!selectedStrategy" @click="openManage">管理策略</el-button>
      <el-button :disabled="!selectedStrategy || loading || publishing" :loading="publishing"
        title="重新发布服务端最新已保存策略，不修改 IP 或访问模式" @click="republishCurrent">重新发布已保存策略</el-button>
      <el-tag v-if="selectedStrategy" :type="publishState.includes('失败') ? 'danger' : publishState === '已生效' ? 'success' : 'warning'">
        {{ publishing ? '正在生效' : publishState }}
      </el-tag>
    </section>

    <div v-if="!selectedStrategy" class="empty-strategy">
      <div><strong>暂无策略，以下为完整功能预览</strong><p>当前默认允许访问；创建策略后即可使用下面全部设置。</p></div>
      <el-button type="primary" @click="openCreate">创建第一项策略</el-button>
    </div>

    <div class="strategy-workspace" :class="{ 'is-disabled': !selectedStrategy }" :aria-disabled="!selectedStrategy">
      <section class="priority-board">
        <div class="priority-board-heading">
          <div><strong>规则优先级</strong><p>黑名单始终最高优先；白名单命中后立即允许访问。</p></div>
          <el-tag type="success">默认动作：ALLOW</el-tag>
        </div>
        <div class="priority-layers">
          <article v-for="layer in priorityLayers" :key="layer.weight" class="priority-layer" :class="layer.tone">
            <span class="weight">{{ layer.weight }}</span><strong>{{ layer.title }}</strong><p>{{ layer.subtitle }}</p>
          </article>
        </div>
      </section>

      <div class="policy-layout">
        <aside class="policy-sidebar">
          <el-card>
            <template #header>{{ selectedStrategy?.name || '策略配置预览' }}</template>
            <p class="status-help">绑定域名：{{ boundDomainNames }}</p>
            <article class="policy-item active"><strong>个别 IP 模式</strong><span>{{ modeLabel(configuration.ipWhitelistMode) }}</span></article>
            <article class="policy-item"><strong>IP 集合模式</strong><span>{{ modeLabel(configuration.collectionWhitelistMode) }}</span></article>
            <article class="policy-item"><strong>国家/地区模式</strong><span>{{ modeLabel(configuration.countryWhitelistMode) }}</span></article>
            <article class="policy-item"><strong>个别 IP / 网段</strong><span>白名单 {{ activeRuleCount('whitelistCidrs') }} 条 · 黑名单 {{ activeRuleCount('blacklistCidrs') }} 条</span></article>
            <article class="policy-item"><strong>国家/地区访问</strong><span>允许 {{ configuration.allowedCountries.length }} 个 · 禁止 {{ configuration.deniedCountries.length }} 个</span></article>
            <article class="policy-guide-card"><strong>自动生效</strong><p>同一版块 1 秒没有新操作后自动发布。</p><p>失败域名继续使用上一版，并显示具体原因。</p></article>
          </el-card>
        </aside>

        <main class="policy-detail">
          <el-card class="rule-card">
            <template #header><div class="rule-header"><span>1000 · 个别 IP / 网段</span><el-tag type="danger">最高优先级</el-tag></div></template>
            <div class="mode-panel">
              <strong>个别 IP 白名单工作方式</strong>
              <el-radio-group v-model="configuration.ipWhitelistMode" @change="schedulePublish('个别 IP 模式')">
                <el-radio-button value="PRIORITY_ALLOW">优先访问</el-radio-button>
                <el-radio-button value="ONLY_WHITELIST">只可访问</el-radio-button>
              </el-radio-group>
              <small v-if="showTimingFor('个别 IP 模式')" class="timing-hint">{{ publishTimingHint }}</small>
              <p>{{ configuration.ipWhitelistMode === 'ONLY_WHITELIST' ? '配置白名单后，只有名单内 IP 可以访问；未配置时不拦截。' : '白名单命中立即允许；未命中继续检查下一版块。' }}</p>
            </div>
            <div class="dual-grid">
              <div class="ip-editor-field">
                <div class="ip-field-label">白名单 IP / 网段</div>
                <div class="ip-chip-editor allow" data-test="whitelist-ip-editor">
                  <el-tag v-for="cidr in configuration.whitelistCidrs" :key="`w-${cidr}`" type="success" effect="plain" closable :disable-transitions="true" @close="removeRule('whitelistCidrs', cidr)">{{ cidr }}</el-tag>
                  <textarea ref="whitelistInputElement" v-model="directAllowInput" rows="1" placeholder="粘贴或输入 IP，可修改后按回车添加" @keydown="handleIpEditorKeydown('whitelistCidrs', $event)" @blur="addRules('whitelistCidrs')" />
                </div>
                <div class="editor-footer"><div><button v-if="removalHistory.whitelistCidrs.length" type="button" class="undo-batch" @click="undoRuleRemovals('whitelistCidrs')">已删除 {{ removalHistory.whitelistCidrs.length }} 条 · 撤销</button><div class="publication-feedback" :class="`is-${feedbackFor('whitelistCidrs').phase}`" data-test="whitelist-feedback" role="status" aria-live="polite" aria-atomic="true"><strong>{{ feedbackFor('whitelistCidrs').title }}</strong><span>{{ feedbackFor('whitelistCidrs').detail }}</span><small v-if="showTimingFor('白名单') && ['active', 'failed'].includes(publicationPhase)" class="timing-hint">{{ publishTimingHint }}</small></div></div><el-button size="small" type="success" plain :disabled="!directAllowInput.trim()" @click.prevent="addRules('whitelistCidrs')">添加 IP</el-button></div>
              </div>
              <div class="ip-editor-field">
                <div class="ip-field-label">黑名单 IP / 网段</div>
                <div class="ip-chip-editor block" data-test="blacklist-ip-editor">
                  <el-tag v-for="cidr in configuration.blacklistCidrs" :key="`b-${cidr}`" type="danger" effect="plain" closable :disable-transitions="true" @close="removeRule('blacklistCidrs', cidr)">{{ cidr }}</el-tag>
                  <textarea ref="blacklistInputElement" v-model="directBlockInput" rows="1" placeholder="粘贴或输入 IP，可修改后按回车添加" @keydown="handleIpEditorKeydown('blacklistCidrs', $event)" @blur="addRules('blacklistCidrs')" />
                </div>
                <div class="editor-footer"><div><button v-if="removalHistory.blacklistCidrs.length" type="button" class="undo-batch" @click="undoRuleRemovals('blacklistCidrs')">已删除 {{ removalHistory.blacklistCidrs.length }} 条 · 撤销</button><div class="publication-feedback" :class="`is-${feedbackFor('blacklistCidrs').phase}`" data-test="blacklist-feedback" role="status" aria-live="polite" aria-atomic="true"><strong>{{ feedbackFor('blacklistCidrs').title }}</strong><span>{{ feedbackFor('blacklistCidrs').detail }}</span><small v-if="showTimingFor('黑名单') && ['active', 'failed'].includes(publicationPhase)" class="timing-hint">{{ publishTimingHint }}</small></div></div><el-button size="small" type="danger" plain :disabled="!directBlockInput.trim()" @click.prevent="addRules('blacklistCidrs')">添加 IP</el-button></div>
              </div>
            </div>
          </el-card>

          <el-card class="rule-card">
            <template #header><div class="rule-header"><span>900 · IP 集合</span><el-tag type="warning">账号内共享</el-tag></div></template>
            <div class="mode-panel">
              <strong>IP 集合白名单工作方式</strong>
              <el-radio-group v-model="configuration.collectionWhitelistMode" @change="schedulePublish('IP 集合')">
                <el-radio-button value="PRIORITY_ALLOW">优先访问</el-radio-button>
                <el-radio-button value="ONLY_WHITELIST">只可访问</el-radio-button>
              </el-radio-group>
              <small v-if="showTimingFor('IP 集合')" class="timing-hint">{{ publishTimingHint }}</small>
              <!-- 更新时间：2026-09-06 10:20 CST（v0.1.88）：白名单跨版块按 OR 合并，黑名单始终优先。 -->
              <p>{{ configuration.collectionWhitelistMode === 'ONLY_WHITELIST' ? '启用白名单限制后，命中任一 IP、集合或国家白名单即可访问；全部未命中时拦截。' : '命中白名单集合立即允许；未命中继续检查其它白名单。' }}</p>
            </div>
            <p class="status-help">修改公共 IP 集合后，所有引用策略会自动发布。</p>
            <div class="dual-grid">
              <label>白名单集合<el-select v-model="configuration.whitelistCollectionIds" multiple filterable placeholder="选择白名单集合" @change="schedulePublish('IP 集合')"><el-option v-for="item in ipCollections" :key="item.id" :label="item.collectionName" :value="item.id" /></el-select></label>
              <label>黑名单集合<el-select v-model="configuration.blacklistCollectionIds" multiple filterable placeholder="选择黑名单集合" @change="schedulePublish('IP 集合')"><el-option v-for="item in ipCollections" :key="item.id" :label="item.collectionName" :value="item.id" /></el-select></label>
            </div>
            <p v-if="!ipCollections.length" class="status-help">暂无集合，请先到“IP 集合”页面创建。</p>
          </el-card>

          <el-card class="rule-card">
            <template #header><div class="rule-header"><span>800 · 国家/地区</span><el-tag type="success">中文选择</el-tag></div></template>
            <div class="mode-panel">
              <strong>允许国家/地区工作方式</strong>
              <el-radio-group v-model="configuration.countryWhitelistMode" @change="schedulePublish('国家/地区')">
                <el-radio-button value="PRIORITY_ALLOW">优先访问</el-radio-button>
                <el-radio-button value="ONLY_WHITELIST">只可访问</el-radio-button>
              </el-radio-group>
              <small v-if="showTimingFor('国家/地区')" class="timing-hint">{{ publishTimingHint }}</small>
              <p>{{ configuration.countryWhitelistMode === 'ONLY_WHITELIST' ? '启用白名单限制后，命中任一 IP、集合或国家白名单即可访问；任一黑名单仍优先拦截。' : '命中允许国家立即放行；未命中继续执行未知地区和默认规则。' }}</p>
            </div>
            <div class="dual-grid">
              <label>允许国家/地区<el-select v-model="configuration.allowedCountries" multiple filterable placeholder="选择允许国家" @change="schedulePublish('国家/地区')"><el-option v-for="item in countryOptions" :key="item.code" :label="`${item.name}（${item.code}）`" :value="item.code" /></el-select></label>
              <label>禁止国家/地区<el-select v-model="configuration.deniedCountries" multiple filterable placeholder="选择禁止国家" @change="schedulePublish('国家/地区')"><el-option v-for="item in countryOptions" :key="item.code" :label="`${item.name}（${item.code}）`" :value="item.code" /></el-select></label>
            </div>
          </el-card>

          <el-card class="rule-card compact-card">
            <template #header><div class="rule-header"><span>700 · 未知地区处理</span><el-tag type="info">GeoIP</el-tag></div></template>
            <label>GeoIP 无法识别时<el-select v-model="configuration.unknownCountryAction" @change="schedulePublish('未知地区')"><el-option label="放行" value="ALLOW" /><el-option label="观察记录" value="OBSERVE" /><el-option label="拦截" value="BLOCK" /></el-select><small v-if="showTimingFor('未知地区')" class="timing-hint">{{ publishTimingHint }}</small></label>
          </el-card>

          <el-card class="rule-card compact-card">
            <template #header><div class="rule-header"><span>模拟验证</span><el-tag>只读检查</el-tag></div></template>
            <p class="status-help">输入访问者 IP，检查当前策略是否允许访问并展示判断过程。</p>
            <div class="simulate-row"><el-input v-model="simulateIp" placeholder="例如 203.0.113.8" @keyup.enter="runSimulation" /><el-button type="primary" @click="runSimulation">模拟匹配</el-button></div>
            <div v-if="simulation" class="simulation-decision" :class="{ 'is-blocked': !simulation.allowed }">
              <div class="simulation-decision-head"><el-tag :type="simulation.allowed ? 'success' : 'danger'">{{ simulation.allowed ? '允许访问' : '禁止访问' }}</el-tag><strong>{{ simulation.reason }}</strong></div>
              <div class="simulation-meta"><span>策略：{{ simulation.strategyName }}</span><span>访问 IP：{{ simulation.clientIp }}</span><span>命中层级：{{ simulation.matchedLayer }}</span><span v-if="simulation.matchedCidr">命中范围：{{ simulation.matchedCidr }}</span></div>
              <ol class="simulation-steps"><li v-for="(step, index) in simulation.evaluationSteps" :key="`${step.layer}-${index}`"><strong>{{ step.layer }}</strong><span>{{ step.outcome }}</span><p>{{ step.reason }}</p></li></ol>
            </div>
          </el-card>
        </main>
      </div>
    </div>

    <el-dialog v-model="createVisible" title="创建策略" width="min(560px, 94vw)">
      <el-form label-position="top"><el-form-item label="策略名称"><el-input v-model="createName" maxlength="30" show-word-limit placeholder="例如：策略一" /></el-form-item><el-form-item label="绑定域名（可以暂不选择）"><el-select v-model="createDomainIds" multiple filterable placeholder="选择一个或多个域名" class="full-width"><el-option v-for="domain in availableDomains" :key="domain.id" :value="domain.id" :label="domain.strategyName ? `${domain.domain}（当前：${domain.strategyName}）` : domain.domain" /></el-select></el-form-item></el-form>
      <template #footer><el-button @click="createVisible = false">取消</el-button><el-button type="primary" :loading="loading" @click="createStrategy">创建并切换</el-button></template>
    </el-dialog>

    <el-dialog v-model="manageVisible" title="管理策略" width="min(620px, 94vw)">
      <el-form label-position="top"><el-form-item label="策略名称"><el-input v-model="manageName" maxlength="30" show-word-limit /></el-form-item><el-form-item label="绑定域名"><el-select v-model="manageDomainIds" multiple filterable class="full-width"><el-option v-for="domain in availableDomains" :key="domain.id" :value="domain.id" :label="domain.strategyName && domain.strategyName !== selectedStrategy?.name ? `${domain.domain}（当前：${domain.strategyName}）` : domain.domain" /></el-select></el-form-item></el-form>
      <template #footer><el-button type="danger" plain @click="deleteStrategy">删除策略</el-button><el-button @click="manageVisible = false">取消</el-button><el-button type="primary" :loading="loading" @click="updateStrategy">保存管理设置</el-button></template>
    </el-dialog>
  </section>
</template>

<style scoped>
.strategy-page { color: #203028; }
.page-title-row,.strategy-toolbar,.strategy-picker,.priority-board-heading,.rule-header,.simulate-row,.simulation-decision-head,.simulation-meta { display:flex; align-items:center; gap:14px; }
.page-title-row,.priority-board-heading,.rule-header { justify-content:space-between; }
.strategy-toolbar { margin-bottom:20px; padding:16px 18px; border:1px solid rgba(23,94,70,.14); border-radius:18px; background:linear-gradient(120deg,#f7fbf5,#edf6ee); }
.strategy-picker { flex:1; }.strategy-picker .el-select { width:min(560px,100%); }.strategy-picker small { float:right; color:#718078; }.full-width { width:100%; }
.empty-strategy { display:flex;align-items:center;justify-content:space-between;gap:18px;padding:18px 22px;margin-bottom:20px;border:1px dashed #91ac9f;border-radius:18px;background:#f7fbf5; }.empty-strategy strong{font-size:18px}.empty-strategy p{margin:6px 0 0;color:#587067}.strategy-workspace.is-disabled{pointer-events:none;user-select:none;opacity:.52;filter:saturate(.55)}
.priority-board { padding:22px; margin-bottom:22px; border:1px solid rgba(23,94,70,.16); border-radius:24px; background:linear-gradient(135deg,rgba(255,255,255,.96),rgba(234,245,235,.76)); box-shadow:0 18px 50px rgba(25,76,55,.08); }
.priority-board-heading p,.status-help { margin:8px 0 0; color:#587067; line-height:1.7; }
.priority-layers { display:grid; grid-template-columns:repeat(5,minmax(150px,1fr)); gap:12px; margin-top:18px; }
.priority-layer { min-height:142px; padding:18px; border:1px solid rgba(32,48,40,.08); border-radius:18px; background:rgba(255,255,255,.78); }
.priority-layer .weight { display:inline-flex; padding:8px 14px; margin-bottom:14px; border-radius:999px; background:#0f6b4e; color:#fff; font-weight:800; }.priority-layer strong,.policy-item strong{display:block}.priority-layer p{color:#63776f;line-height:1.6}.priority-layer.danger .weight{background:#b6332f}.priority-layer.warning .weight{background:#c57b14}.priority-layer.info .weight{background:#2f6d8f}.priority-layer.muted .weight{background:#65736d}
.policy-layout { display:grid; grid-template-columns:340px minmax(0,1fr); gap:22px; align-items:start; }.policy-sidebar{position:sticky;top:16px}.policy-detail{display:grid;gap:18px}.rule-card{border-radius:22px}
.policy-item,.policy-guide-card,.mode-panel { padding:16px; margin-top:14px; border:1px solid rgba(22,101,72,.12); border-radius:18px; background:rgba(241,248,238,.74); }.policy-item span{display:block;margin-top:8px;color:#446259}.policy-item.active{border-left:5px solid #0f6b4e}.mode-panel{display:grid;gap:12px}
.dual-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:18px; margin-top:18px; }.ip-editor-field,.compact-card label{display:grid;gap:10px;color:#314c42}.ip-field-label,.compact-card label{font-weight:700}
.ip-chip-editor{display:flex;align-content:flex-start;align-items:center;flex-wrap:wrap;gap:7px;min-height:78px;max-height:190px;padding:10px 12px;overflow-y:auto;border:1px solid #d7ded9;border-radius:8px;background:#fff;cursor:text;transition:border-color .18s,box-shadow .18s}.ip-chip-editor:focus-within{border-color:#409eff;box-shadow:0 0 0 1px #409eff inset}.ip-chip-editor.allow .el-tag{--el-tag-bg-color:#eef8ee;--el-tag-border-color:#b8dfc4;--el-tag-text-color:#185b39}.ip-chip-editor.block .el-tag{--el-tag-bg-color:#fff1ef;--el-tag-border-color:#f0c1bc;--el-tag-text-color:#9b2c2c}.ip-chip-editor textarea{flex:1 1 190px;min-width:150px;min-height:30px;padding:5px 2px;resize:none;border:0;outline:0;background:transparent;color:#203028;font:inherit;font-weight:400;line-height:20px}.ip-chip-editor textarea::placeholder{color:#a7afb0}.editor-footer{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;min-height:30px}.editor-footer>div{display:grid;gap:4px}.undo-batch{width:max-content;padding:0;border:0;border-bottom:1px solid currentColor;background:transparent;color:#a55f00;font:inherit;font-size:12px;cursor:pointer}.undo-batch:focus-visible{outline:2px solid #409eff;outline-offset:3px}.compact-card{max-width:820px}.simulate-row{margin-top:14px}.simulate-row .el-input{flex:1}
.timing-hint{color:#5c7469;font-size:12px;font-weight:500;line-height:1.4;white-space:nowrap}.editor-timing{display:block;margin-top:2px}
.publication-feedback{display:grid;gap:4px;min-height:44px;padding-top:2px;color:#546c60;font-weight:400;line-height:1.5;overflow-wrap:anywhere}.publication-feedback strong{font-size:13px;font-weight:700}.publication-feedback span{font-size:12px}.publication-feedback.is-active strong{color:#176548}.publication-feedback.is-queued strong,.publication-feedback.is-saving strong,.publication-feedback.is-publishing strong,.publication-feedback.is-pending strong{color:#285e91}.publication-feedback.is-unknown strong,.publication-feedback.is-changed strong{color:#925400}.publication-feedback.is-failed strong,.publication-feedback.is-failed span{color:#a5322a}.editor-footer>div{flex:1;min-width:0}.editor-footer .el-button{flex-shrink:0}
.simulation-decision{margin-top:14px;padding:16px;border:1px solid rgba(23,74,53,.16);border-radius:16px;background:#f3faf4}.simulation-decision.is-blocked{background:#fff5f3}.simulation-meta{flex-wrap:wrap;margin-top:12px;color:#587067;font-size:13px}.simulation-steps{display:grid;gap:8px;margin:14px 0 0;padding:0;list-style:none}.simulation-steps li{display:grid;grid-template-columns:minmax(150px,auto) 76px 1fr;gap:12px;padding-top:8px;border-top:1px dashed rgba(23,74,53,.16)}.simulation-steps p{margin:0;color:#587067}
@media(max-width:1180px){.priority-layers{grid-template-columns:repeat(2,minmax(0,1fr))}.policy-layout{grid-template-columns:1fr}.policy-sidebar{position:static}}
@media(max-width:760px){.page-title-row,.strategy-toolbar,.strategy-picker,.priority-board-heading,.rule-header,.simulate-row,.empty-strategy{align-items:stretch;flex-direction:column}.priority-layers,.dual-grid{grid-template-columns:1fr}.simulation-steps li{grid-template-columns:1fr}.strategy-picker .el-select{width:100%}}
</style>
